import classNames from "classnames"
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { v4 as uuidv4 } from "uuid"

import { getConfigEnv } from "./config"

export function useConfig() {
  // const [config, setConfig] = useState(getConfigEnv(window._env_))

  // useEffect(() => {
  //   setConfig(getConfigEnv(window._env_))
  // }, [])

  return getConfigEnv(window._env_)
}

export function useWindowSize() {
  const [size, setSize] = useState([0, 0])

  useLayoutEffect(() => {
    function updateSize() {
      setSize([window.innerWidth, window.innerHeight])
    }

    window.addEventListener("resize", updateSize)
    updateSize()

    return () => window.removeEventListener("resize", updateSize)
  }, [])

  return size
}

export function useTooltip() {
  const initialState = {
    text: "",
    top: "-300vh",
    left: "-300vw",
    customStyle: {},
    caretBottom: false,
    caretTop: false,
    caretLeft: false,
    caretRight: false,
    customClassName: {},
    replaceTopWithBottom: false,
    alignItemAbove: false,
  }
  const [tooltipState, setTooltipState] = useState(initialState)
  const [focus, setFocus] = useState(false)

  const topCaret =
    tooltipState.caretTop &&
    !tooltipState.caretBottom &&
    !tooltipState.caretLeft &&
    !tooltipState.caretRight

  const bottomCaret =
    tooltipState.caretBottom &&
    !tooltipState.caretTop &&
    !tooltipState.caretLeft &&
    !tooltipState.caretRight

  const leftCaret =
    tooltipState.caretLeft &&
    !tooltipState.caretTop &&
    !tooltipState.caretBottom &&
    !tooltipState.caretRight

  const rightCaret =
    tooltipState.caretRight &&
    !tooltipState.caretTop &&
    !tooltipState.caretBottom &&
    !tooltipState.caretLeft

  const tooltipCaret = classNames("caret", {
    "caret-bottom": bottomCaret,
    "caret-top": topCaret,
    "caret-left": leftCaret,
    "caret-right": rightCaret,
    [tooltipState.customClassName]: !!tooltipState.customClassName,
  })

  const setTooltip = ({
    event,
    text,
    topPos = 60,
    leftPos = 0,
    customStyle = {},
    caretTop = false,
    caretLeft = false,
    caretRight = false,
    caretBottom = false,
    customClassName = "",
    replaceTopWithBottom = false,
    alignItemAbove = false,
  }) => {
    const { top, left, right } = event.target.getBoundingClientRect()
    setTooltipState(() => ({
      text,
      top: replaceTopWithBottom ? `${top - 10}px` : `${top - topPos}px`,
      left: alignItemAbove ? (left + right) / 2 - leftPos : `${left - leftPos}px`,
      customStyle,
      caretBottom,
      caretTop,
      caretLeft,
      caretRight,
      customClassName,
      replaceTopWithBottom,
      alignItemAbove,
    }))
  }

  const closeTooltip = () => {
    if (!focus) {
      setTooltipState(initialState)
    }
  }

  useEffect(() => {
    closeTooltip()
  }, [focus])

  const tooltip = useMemo(() => {
    const caret =
      tooltipState.caretBottom ||
      tooltipState.caretLeft ||
      tooltipState.caretRight ||
      tooltipState.caretTop
    return (
      <div
        onMouseEnter={() => {
          setFocus(true)
        }}
        onMouseLeave={() => {
          setFocus(false)
        }}
        className="hook-tooltip"
        style={{
          position: "fixed",
          top: tooltipState.replaceTopWithBottom ? null : tooltipState.top,
          left: tooltipState.left,
          transform: tooltipState.alignItemAbove ? "translateX(-50%)" : "none",
          color: tooltipState.color,
          ...tooltipState.customStyle,
          bottom: tooltipState.replaceTopWithBottom ? `calc(100vh - ${tooltipState.top})` : null,
          zIndex: 100001,
        }}
      >
        <p className="hook-tooltip-text small text-invert">
          {typeof tooltipState.text === "string"
            ? tooltipState.text?.split("\n").map(line => (
                <React.Fragment key={uuidv4()}>
                  {line} <br />
                </React.Fragment>
              ))
            : tooltipState.text}
        </p>
        {caret && <span className={tooltipCaret} />}
      </div>
    )
  }, [tooltipState, focus])

  return [tooltip, setTooltip, closeTooltip]
}

export function useCustomMenu(className) {
  const initialState = {
    top: "-300vh",
    left: "-300vw",
    element: "",
    active: false,
  }

  const elementRef = useRef()

  const [menuState, setMenuState] = useState(initialState)

  const setCustomMenu = (
    event,
    element,
    additionalLeft = 0,
    additionalTop = 0,
    withoutInnerHeightCondition = false,
  ) => {
    event.stopPropagation()

    if (window.currentOpenMenu && window.currentOpenMenu !== setMenuState) {
      window.currentOpenMenu(initialState)
    }

    const { top, left } = event.currentTarget.getBoundingClientRect()
    setMenuState(() => ({
      top:
        top + 345 < window.innerHeight || withoutInnerHeightCondition
          ? `${top + additionalTop}px`
          : `${top + additionalTop - 242}px`,
      left: `${left - 240 + additionalLeft}px`,
      element,
      active: true,
    }))

    window.currentOpenMenu = setMenuState
  }

  useEffect(() => {
    const closeMenuOnClickOutside = e => {
      if (e.target !== elementRef.current && menuState.active) {
        setMenuState(initialState)
        window.currentOpenMenu = null
      }
    }

    const closeMenuOnScroll = () => {
      setMenuState(initialState)
      window.currentOpenMenu = null
    }

    document.addEventListener("click", closeMenuOnClickOutside)
    document.addEventListener("scroll", closeMenuOnScroll)

    return () => {
      document.removeEventListener("click", closeMenuOnClickOutside)
      document.removeEventListener("scroll", closeMenuOnScroll)
    }
  }, [menuState.active])

  const customMenu = (
    <div
      ref={elementRef}
      className={className}
      style={{
        position: "fixed",
        top: menuState.top,
        left: menuState.left,
      }}
    >
      {menuState.element}
    </div>
  )

  return [customMenu, setCustomMenu]
}

export function useOnClickOutside(ref, handler) {
  useEffect(
    () => {
      const listener = event => {
        /** Do nothing if clicking ref's element or descendent elements */
        if (!ref.current || ref.current.contains(event.target)) {
          return
        }

        handler(event)
      }

      document.addEventListener("mousedown", listener)
      document.addEventListener("touchstart", listener)

      return () => {
        document.removeEventListener("mousedown", listener)
        document.removeEventListener("touchstart", listener)
      }
    },
    /**
     * Add ref and handler to effect dependencies
     * It's worth noting that because passed in handler is a new function on every render
     * that will cause this effect callback/cleanup to run every render. It's not a big deal,
     * but to optimize you can wrap handler in useCallback before passing it into this hook.
     */
    [ref, handler],
  )
}
